import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Dashboard.css";
import { COURSE_CATALOG, COURSE_PROGRESS_KEY } from "../data/courseCatalog";
import { getAuthHeader } from "../utils/auth";
import { fetchCurrentUser, getApiBaseUrl, storeApiBaseUrl } from "../utils/api";
import { getLocalizedBlog } from "../utils/localizedBlog";
import { useTranslation } from "../i18n";

const API_URL = getApiBaseUrl();
const RECENT_CONTENT_PAGE_SIZE = 10;
const RECENT_CONTENT_NEW_DAYS = 14;
const NOTIFICATIONS_PAGE_SIZE = 3;
const RECENT_CONTENT_FALLBACK = [
  { id: "v1", kind: "Vídeo", title: "Herramientas que todo QA debería conocer en 2025", link: "https://www.youtube.com/watch?v=O-blvvETkSo", publishedAt: "2025-01-20T00:00:00.000Z" },
  { id: "b1", kind: "Blog", title: "Recomendaciones para principiantes", link: "/blog/67aa57375fcb462971544ca9", publishedAt: "2025-02-11T00:00:00.000Z" },
  { id: "v2", kind: "Vídeo", title: "Cypress: Querying (cy.get, contains, selectors)", link: "https://www.youtube.com/watch?v=jzgn377TgWA", publishedAt: "2024-12-01T00:00:00.000Z" },
];

const getDismissedNotificationsStorageKey = (userId) =>
  `theqahub_dismissed_dashboard_notifications:${userId || "guest"}`;

function Dashboard() {
  const navigate = useNavigate();
  const { language } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [error, setError] = useState("");
  const [roadmapProgress, setRoadmapProgress] = useState({});
  const [courseProgress, setCourseProgress] = useState({});
  const [challengeProgress, setChallengeProgress] = useState({});
  const [recentContent, setRecentContent] = useState(RECENT_CONTENT_FALLBACK);
  const [recentContentPage, setRecentContentPage] = useState(1);
  const [recentContentFilter, setRecentContentFilter] = useState("all");
  const [notifications, setNotifications] = useState([]);
  const [notificationsPage, setNotificationsPage] = useState(1);
  const [dismissedNotificationKeys, setDismissedNotificationKeys] = useState([]);
  const [apiBaseUrl, setApiBaseUrl] = useState(API_URL);

  const courses = useMemo(() => {
    return COURSE_CATALOG.map((course) => {
      const completedVideos = courseProgress[course.id] || {};
      const completedCount = course.videos.filter((video) => completedVideos[video.id]).length;
      const progress = course.videos.length
        ? Math.round((completedCount / course.videos.length) * 100)
        : 0;
      const lastCompleted = [...course.videos].reverse().find((video) => completedVideos[video.id]);
      const firstVideo = course.videos[0];
      const lastLesson = lastCompleted?.title || firstVideo?.title || "Sin vídeos";
      const status =
        completedCount === 0
          ? "not_started"
          : completedCount === course.videos.length
            ? "completed"
            : "in_progress";

      return {
        ...course,
        progress,
        lastLesson,
        completedCount,
        status,
      };
    });
  }, [courseProgress]);

  const roadmap = useMemo(
    () => [
      { id: "fundamentos", title: "Fundamentos QA" },
      { id: "manual", title: "Testing Manual" },
      { id: "automation", title: "Automatización Web" },
      { id: "api", title: "API Testing" },
      { id: "cicd", title: "CI/CD para QA" },
      { id: "buenas", title: "Buenas prácticas & Arquitectura" },
    ],
    []
  );

  const challenges = useMemo(
    () => [
      { id: "reto1", title: "Diseña casos de prueba para un Login", type: "Manual", difficulty: "Fácil", time: "15 min" },
      { id: "reto2", title: "Valida un endpoint GET/POST con Postman", type: "API", difficulty: "Media", time: "25 min" },
      { id: "reto3", title: "Crea un test E2E de carrito de compra", type: "Automation", difficulty: "Media", time: "35 min" },
    ],
    []
  );

  const tools = useMemo(
    () => [
      {
        id: "cypress",
        name: "Cypress",
        desc: "E2E moderno y rápido",
        link: "https://www.youtube.com/watch?v=OH3H1F2hbkI&list=PLzu7exPjIiXAa3zuGIugkzPKXNtauKHon&pp=sAgC",
      },
      {
        id: "cucumber",
        name: "Cucumber",
        desc: "BDD y escenarios legibles",
        link: "https://www.youtube.com/watch?v=l6PzkkF54uA&list=PLzu7exPjIiXC1_fuUP-jGnwkmfgZgkXDL&pp=sAgC",
      },
      {
        id: "postman",
        name: "Postman + Newman",
        desc: "API testing y ejecución por CLI",
        link: "https://www.youtube.com/watch?v=E_dg9ntCc4c&list=PLzu7exPjIiXAhB3Kijyccyb-TSZkZs23k&pp=sAgC",
      },
      {
        id: "playwright",
        name: "Playwright",
        desc: "E2E potente multi-browser",
        link: "https://www.youtube.com/watch?v=RDsJl_reY70&list=PLzu7exPjIiXA3KFuPWjNb0YHwdE9gjRdl&pp=sAgC",
      },
      {
        id: "selenium",
        name: "Selenium",
        desc: "Clásico para automatización",
      },
      {
        id: "appium",
        name: "Appium",
        desc: "Automatización móvil",
      },
      {
        id: "github",
        name: "GitHub",
        desc: "Control de versiones",
      },
      {
        id: "ci",
        name: "CI/CD",
        desc: "Pipelines y calidad continua",
        link: "https://www.youtube.com/watch?v=SpTLmJWADBE&list=PLzu7exPjIiXA8_i7XBTFN6AdGm53mHlNi&index=4&pp=iAQB0gcJCdkKAYcqIYzvsAgC",
      },
    ],
    []
  );

  const latestContent = useMemo(() => {
    return [...recentContent]
      .sort((a, b) => new Date(b.publishedAt || 0) - new Date(a.publishedAt || 0));
  }, [recentContent]);

  const filteredLatestContent = useMemo(() => {
    if (recentContentFilter === "all") return latestContent;
    return latestContent.filter((item) => item.kind === recentContentFilter);
  }, [latestContent, recentContentFilter]);

  const latestContentTotalPages = Math.max(1, Math.ceil(filteredLatestContent.length / RECENT_CONTENT_PAGE_SIZE));

  const latestContentPageItems = useMemo(() => {
    const startIndex = (recentContentPage - 1) * RECENT_CONTENT_PAGE_SIZE;
    return filteredLatestContent.slice(startIndex, startIndex + RECENT_CONTENT_PAGE_SIZE);
  }, [filteredLatestContent, recentContentPage]);

  const userId = user?._id || user?.email || "";

  const baseNotifications = useMemo(
    () => [
      {
        id: "system-welcome",
        title: "Bienvenido a TheQAHub Academy",
        desc: "Tu portal está listo. Empieza por la ruta recomendada.",
        kind: "system",
        isNew: false,
      },
    ],
    []
  );

  const temporaryNotifications = useMemo(() => {
    const nextRoadmapStep = roadmap.find((step) => !roadmapProgress?.[step.id]?.completed);
    const inProgressCourses = courses
      .filter((course) => course.progress > 0 && course.progress < 100)
      .sort((a, b) => b.progress - a.progress);
    const incompleteChallenges = challenges.filter(
      (challenge) => !challengeProgress?.[challenge.id]?.completed
    );
    const freshContentNotifications = latestContent
      .filter((item) => item.isNew)
      .slice(0, 2)
      .map((item) => ({
        id: `temp-content-${item.id}`,
        title: `${item.kind} nuevo disponible`,
        desc:
          item.kind === "Vídeo"
            ? `Ya puedes ver "${item.title}".`
            : `Ya puedes leer "${item.title}".`,
        kind: "content",
        isNew: true,
        dismissible: true,
      }));

    return [
      nextRoadmapStep
        ? {
            id: `temp-roadmap-${nextRoadmapStep.id}`,
            title: "Siguiente paso",
            desc: `Continúa con ${nextRoadmapStep.title} para seguir avanzando en tu ruta de estudio.`,
            kind: "roadmap",
            isNew: false,
            dismissible: true,
          }
        : null,
      inProgressCourses[0]
        ? {
            id: `temp-course-${inProgressCourses[0].id}`,
            title: "Curso en progreso",
            desc: `Tienes ${inProgressCourses[0].title} al ${inProgressCourses[0].progress}%. Dale el empujón final para completarlo.`,
            kind: "course-reminder",
            isNew: false,
            dismissible: true,
          }
        : null,
      incompleteChallenges[0]
        ? {
            id: `temp-challenge-${incompleteChallenges[0].id}`,
            title: "Reto recomendado",
            desc: `Prueba el reto "${incompleteChallenges[0].title}" y suma práctica real a tu progreso.`,
            kind: "challenge-reminder",
            isNew: false,
            dismissible: true,
          }
        : null,
      ...freshContentNotifications,
    ]
      .filter(Boolean)
      .filter((item) => !dismissedNotificationKeys.includes(item.id));
  }, [challengeProgress, challenges, courses, dismissedNotificationKeys, latestContent, roadmap, roadmapProgress]);

  const academyNotifications = useMemo(() => {
    const now = Date.now();
    const newThreshold = RECENT_CONTENT_NEW_DAYS * 24 * 60 * 60 * 1000;
    const dynamicNotifications = notifications.map((item) => ({
      id: item.key || item._id || `${item.title}-${item.createdAt}`,
      title: item.title,
      desc: item.message,
      kind: item.kind || "academy",
      createdAt: item.createdAt,
      isNew: now - new Date(item.createdAt || now).getTime() <= newThreshold,
    }));

    return [...temporaryNotifications, ...dynamicNotifications, ...baseNotifications];
  }, [baseNotifications, notifications, temporaryNotifications]);

  const notificationsTotalPages = Math.max(1, Math.ceil(academyNotifications.length / NOTIFICATIONS_PAGE_SIZE));

  const notificationsPageItems = useMemo(() => {
    const startIndex = (notificationsPage - 1) * NOTIFICATIONS_PAGE_SIZE;
    return academyNotifications.slice(startIndex, startIndex + NOTIFICATIONS_PAGE_SIZE);
  }, [academyNotifications, notificationsPage]);

  const overallProgress = useMemo(() => {
    const total = roadmap.length || 1;
    const completed = roadmap.filter(s => roadmapProgress?.[s.id]?.completed).length;
    return Math.round((completed / total) * 100);
  }, [roadmap, roadmapProgress]);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setError("");

      try {
        const { user: currentUser, baseUrl } = await fetchCurrentUser();

        if (!currentUser || !baseUrl) {
          navigate("/login");
          return;
        }

        setUser(currentUser);
        setApiBaseUrl(baseUrl);
        storeApiBaseUrl(baseUrl);

        const [resRoad, resCourse, resNotifications, resChallenges] = await Promise.all([
          fetch(`${baseUrl}/api/auth/roadmap`, {
            method: "GET",
            credentials: "include",
            headers: {
              ...getAuthHeader(),
            },
          }),
          fetch(`${baseUrl}/api/auth/course-progress`, {
            method: "GET",
            credentials: "include",
            headers: {
              ...getAuthHeader(),
            },
          }),
          fetch(`${baseUrl}/api/auth/notifications`, {
            method: "GET",
            credentials: "include",
            headers: {
              ...getAuthHeader(),
            },
          }),
          fetch(`${baseUrl}/api/auth/challenge-progress`, {
            method: "GET",
            credentials: "include",
            headers: {
              ...getAuthHeader(),
            },
          }),
        ]);

        const dataRoad = await resRoad.json().catch(() => ({}));
        const dataCourse = await resCourse.json().catch(() => ({}));
        const dataNotifications = await resNotifications.json().catch(() => ({}));
        const dataChallenges = await resChallenges.json().catch(() => ({}));

        if (resRoad.ok) setRoadmapProgress(dataRoad.roadmap || {});
        if (resNotifications.ok) setNotifications(dataNotifications.notifications || []);
        if (resChallenges.ok) setChallengeProgress(dataChallenges.challengeProgress || {});

        if (resCourse.ok) {
          const serverProgress = dataCourse.courseProgress || {};
          const hasServerProgress = Object.keys(serverProgress).length > 0;

          if (!hasServerProgress) {
            try {
              const raw = window.localStorage.getItem(COURSE_PROGRESS_KEY);
              const legacyProgress = raw ? JSON.parse(raw) : {};
              const hasLegacyProgress = Object.keys(legacyProgress || {}).length > 0;

              if (hasLegacyProgress) {
                const syncRes = await fetch(`${baseUrl}/api/auth/course-progress/sync`, {
                  method: "POST",
                  credentials: "include",
                  headers: {
                    "Content-Type": "application/json",
                    ...getAuthHeader(),
                  },
                  body: JSON.stringify({ courseProgress: legacyProgress }),
                });

                const syncData = await syncRes.json().catch(() => ({}));
                if (syncRes.ok) {
                  setCourseProgress(syncData.courseProgress || {});
                  window.localStorage.removeItem(COURSE_PROGRESS_KEY);
                } else {
                  setCourseProgress(legacyProgress);
                }
              } else {
                setCourseProgress({});
              }
            } catch {
              setCourseProgress(serverProgress);
            }
          } else {
            setCourseProgress(serverProgress);
            try {
              window.localStorage.removeItem(COURSE_PROGRESS_KEY);
            } catch {}
          }
        }

      } catch (e) {
        setError("No se pudo conectar con el servidor.");
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [navigate]);

  useEffect(() => {
    setRecentContentPage(1);
  }, [latestContent.length]);

  useEffect(() => {
    setRecentContentPage(1);
  }, [recentContentFilter]);

  useEffect(() => {
    setNotificationsPage(1);
  }, [academyNotifications.length]);

  useEffect(() => {
    if (!userId || typeof window === "undefined") {
      setDismissedNotificationKeys([]);
      return;
    }

    try {
      const raw = window.localStorage.getItem(getDismissedNotificationsStorageKey(userId));
      const parsed = raw ? JSON.parse(raw) : [];
      setDismissedNotificationKeys(Array.isArray(parsed) ? parsed : []);
    } catch {
      setDismissedNotificationKeys([]);
    }
  }, [userId]);

  useEffect(() => {
    let cancelled = false;
    let refreshTimer = null;

    const normalizeRecentEntries = (items) => {
      const now = Date.now();
      const newThreshold = RECENT_CONTENT_NEW_DAYS * 24 * 60 * 60 * 1000;

      return items
        .filter((item) => item?.title && item?.link && item?.publishedAt)
        .sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt))
        .map((item) => ({
          ...item,
          isNew: now - new Date(item.publishedAt).getTime() <= newThreshold,
        }));
    };

    const fetchRecentContent = async () => {
      try {
        const [blogsResponse, youtubeResponse] = await Promise.all([
          fetch(`${apiBaseUrl}/api/blogs`),
          fetch(`${apiBaseUrl}/api/youtube-feed`),
        ]);

        const blogsData = blogsResponse.ok ? await blogsResponse.json() : [];
        const youtubeText = youtubeResponse.ok ? await youtubeResponse.text() : "";

        const blogEntries = Array.isArray(blogsData)
          ? blogsData.map((blog) => {
              const localizedBlog = getLocalizedBlog(blog, language);
              return {
                id: `blog-${blog._id}`,
                kind: "Blog",
                title: localizedBlog.title || blog.title,
                link: `/blog/${blog._id}`,
                publishedAt: blog.createdAt,
              };
            })
          : [];

        const parser = new DOMParser();
        const xml = parser.parseFromString(youtubeText, "text/xml");
        const items = Array.from(xml.getElementsByTagName("entry"));
        const videoEntries = items.map((item) => ({
          id: `video-${item.getElementsByTagName("yt:videoId")[0]?.textContent || item.getElementsByTagName("videoId")[0]?.textContent || Math.random().toString(36).slice(2)}`,
          kind: "Vídeo",
          title: item.getElementsByTagName("title")[0]?.textContent || "",
          link: item.getElementsByTagName("link")[0]?.getAttribute("href") || "",
          publishedAt: item.getElementsByTagName("published")[0]?.textContent || "",
        }));

        const mergedEntries = normalizeRecentEntries([...blogEntries, ...videoEntries]);

        if (!cancelled && mergedEntries.length > 0) {
          setRecentContent(mergedEntries);
        }
      } catch (error) {
        if (!cancelled) {
          setRecentContent(normalizeRecentEntries(RECENT_CONTENT_FALLBACK));
        }
      }
    };

    fetchRecentContent();
    refreshTimer = window.setInterval(fetchRecentContent, 5 * 60 * 1000);

    return () => {
      cancelled = true;
      if (refreshTimer) window.clearInterval(refreshTimer);
    };
  }, [apiBaseUrl, language]);

  if (loading) {
    return (
      <div className="dash-page">
        <div className="dash-container">
          <div className="dash-skeleton">Cargando dashboard...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dash-page">
        <div className="dash-container">
          <div className="dash-error">{error}</div>
          <button className="dash-btn" onClick={() => navigate("/login")}>Ir al login</button>
        </div>
      </div>
    );
  }

  // Nombre "Diego S."
  const nombre = (user?.nombre || "").trim();
  const apellidos = (user?.apellidos || "").trim();
  const apellidoInicial = apellidos ? apellidos.charAt(0).toUpperCase() + "." : "";
  const displayName = nombre ? `${nombre} ${apellidoInicial}`.trim() : (user?.email ? user.email.split("@")[0] : "QA");
  const nextRoadmap = roadmap.find(s => !roadmapProgress?.[s.id]?.completed)?.title || "Ruta completada";

  const dismissNotification = (notificationId) => {
    if (!userId || typeof window === "undefined") return;

    setDismissedNotificationKeys((current) => {
      if (current.includes(notificationId)) return current;
      const nextKeys = [...current, notificationId];

      try {
        window.localStorage.setItem(
          getDismissedNotificationsStorageKey(userId),
          JSON.stringify(nextKeys)
        );
      } catch {}

      return nextKeys;
    });
  };

  return (
    <div className="dash-page">
      <div className="dash-container">
        {/* HERO */}
        <div className="dash-hero">
          <div>
            <h1>¡Bienvenido, {displayName}!</h1>
            <p className="muted">
              Tu centro de operaciones QA: aprende, practica y sube de nivel sin humo.
            </p>

            <div className="dash-quick">
              <div className="quick-card">
                <div className="quick-title">Progreso general</div>
                <div className="quick-value">{overallProgress}%</div>
                <div className="progress">
                  <div className="progress-bar" style={{ width: `${overallProgress}%` }} />
                </div>
              </div>

              <div className="quick-card">
                <div className="quick-title">Siguiente paso</div>
                <div className="quick-value small">{nextRoadmap}</div>
                <div className="muted small">Continúa con el siguiente paso de la ruta</div>
              </div>

              <div className="quick-card">
                <div className="quick-title">Objetivo</div>
                <div className="quick-value small">QA Automation</div>
                <div className="muted small">Ruta recomendada + retos</div>
              </div>
            </div>
          </div>
        </div>

        {/* GRID */}
        <div className="dash-grid">
          {/* LEFT */}
          <div className="col">

            <section className="dash-card">
              <div className="dash-card-head">
                <h2>Mis cursos</h2>
                <button className="dash-btn ghost dash-section-btn" onClick={() => navigate("/aprende?academy=1")}>Ver todo</button>
              </div>

              <div className="dash-list">
                {courses.map((c) => (
                  <div key={c.id} className="dash-item">
                    <div className="dash-item-main">
                      <div className="dash-item-title">{c.title}</div>
                      <div className="muted small">Última lección: {c.lastLesson}</div>
                      <div className="pill-row">
                        <span className="pill">{c.level}</span>
                        <span className={`pill ${c.status === "completed" ? "pill-green" : "pill-gray"}`}>
                          {c.status === "completed"
                            ? "Completado"
                            : c.status === "in_progress"
                              ? "En progreso"
                              : "No empezado"}
                        </span>
                        <span className="pill pill-gray">
                          {c.completedCount}/{c.videos.length} vídeos
                        </span>
                      </div>
                    </div>

                    <div className="dash-item-side">
                      <div className="dash-percent">{c.progress}%</div>
                      <div className="progress mini">
                        <div className="progress-bar" style={{ width: `${c.progress}%` }} />
                      </div>
                      <button className="dash-btn" onClick={() => navigate(`/learn/${c.id}`)}>
                        Empezar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="dash-card">
              <div className="dash-card-head">
                <h2>Retos QA</h2>
                <button className="dash-btn ghost dash-section-btn" onClick={() => navigate("/retos")}>Ver retos</button>
              </div>

              <div className="dash-list">
                {challenges.map((ch) => (
                  <div key={ch.id} className="dash-item">
                    <div className="dash-item-main">
                      <div className="dash-item-title">{ch.title}</div>
                      <div className="muted small">{ch.type} · {ch.difficulty} · {ch.time}</div>
                      <div className="pill-row">
                        <span className="pill pill-green">{ch.type}</span>
                        <span className="pill pill-gray">{ch.difficulty}</span>
                      </div>
                    </div>

                    <div className="dash-item-side">
                      <button className="dash-btn" onClick={() => navigate(`/retos/${ch.id}`)}>
                        Empezar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="dash-card">
              <div className="dash-card-head recent-content-head">
                <h2>Contenido reciente</h2>
                <div className="dash-card-head-actions">
                  <div className="recent-content-filters">
                    <button
                      type="button"
                      className={`recent-filter-btn ${recentContentFilter === "Vídeo" ? "active video" : ""}`}
                      onClick={() => setRecentContentFilter((current) => current === "Vídeo" ? "all" : "Vídeo")}
                    >
                      Vídeo
                    </button>
                    <button
                      type="button"
                      className={`recent-filter-btn ${recentContentFilter === "Blog" ? "active blog" : ""}`}
                      onClick={() => setRecentContentFilter((current) => current === "Blog" ? "all" : "Blog")}
                    >
                      Blog
                    </button>
                  </div>
                  <button className="dash-btn ghost dash-section-btn" onClick={() => navigate("/")}>Ir al inicio</button>
                </div>
              </div>

              <div className="dash-list recent-content-list">
                {latestContentPageItems.map((it) => (
                  (() => {
                    const isShortTitle = (it.title || "").trim().length <= 38;

                    return (
                  <a
                    key={it.id}
                    className={`dash-link-item recent-content-item ${isShortTitle ? "recent-content-item-short" : ""}`}
                    href={it.link}
                    target={it.link.startsWith("http") ? "_blank" : undefined}
                    rel={it.link.startsWith("http") ? "noreferrer" : undefined}
                  >
                    <span className={`pill ${it.kind === "Vídeo" ? "dash-pill-video" : "dash-pill-blog"}`}>{it.kind}</span>
                    <span className={`dash-link-copy ${isShortTitle ? "dash-link-copy-short" : ""}`}>
                      <span className={`dash-link-title-row ${isShortTitle ? "dash-link-title-row-short" : ""}`}>
                        <span className={`dash-link-title ${isShortTitle ? "dash-link-title-short" : ""}`}>
                          <span className="dash-link-title-text">{it.title}</span>
                        </span>
                        {it.isNew ? <span className="dash-new-badge">NEW</span> : null}
                      </span>
                      <span className="muted small dash-link-date">
                        {new Date(it.publishedAt).toLocaleDateString(
                          language === "de" ? "de-DE" : language === "en" ? "en-US" : "es-ES"
                        )}
                      </span>
                    </span>
                    <span className="dash-arrow">→</span>
                  </a>
                    );
                  })()
                ))}
              </div>

              {latestContentTotalPages > 1 ? (
                <div className="dash-pagination">
                  <button
                    type="button"
                    className="dash-page-btn"
                    onClick={() => setRecentContentPage((page) => Math.max(1, page - 1))}
                    disabled={recentContentPage === 1}
                  >
                    Anterior
                  </button>
                  <span className="dash-page-indicator">
                    Página {recentContentPage} de {latestContentTotalPages}
                  </span>
                  <button
                    type="button"
                    className="dash-page-btn"
                    onClick={() => setRecentContentPage((page) => Math.min(latestContentTotalPages, page + 1))}
                    disabled={recentContentPage === latestContentTotalPages}
                  >
                    Siguiente
                  </button>
                </div>
              ) : null}
            </section>
          </div>

          {/* RIGHT */}
          <div className="col">

            <section className="dash-card">
              <div className="dash-card-head">
                <h2>Notificaciones</h2>
                <button className="dash-btn ghost dash-section-btn" onClick={() => navigate("/settings")}>Ajustes</button>
              </div>

              <div className="dash-list">
                {notificationsPageItems.map((n) => (
                  <div key={n.id} className="notif">
                    <div className="notif-title-row">
                      <div className="notif-title">{n.title}</div>
                      {n.isNew ? <span className="dash-new-badge">NEW</span> : null}
                      {n.dismissible ? (
                        <button
                          type="button"
                          className="notif-dismiss-btn"
                          aria-label={`Descartar notificación ${n.title}`}
                          onClick={() => dismissNotification(n.id)}
                        >
                          ×
                        </button>
                      ) : null}
                    </div>
                    <div className="muted small">{n.desc}</div>
                  </div>
                ))}
              </div>

              {notificationsTotalPages > 1 ? (
                <div className="dash-pagination">
                  <button
                    type="button"
                    className="dash-page-btn"
                    onClick={() => setNotificationsPage((page) => Math.max(1, page - 1))}
                    disabled={notificationsPage === 1}
                  >
                    Anterior
                  </button>
                  <span className="dash-page-indicator">
                    Página {notificationsPage} de {notificationsTotalPages}
                  </span>
                  <button
                    type="button"
                    className="dash-page-btn"
                    onClick={() => setNotificationsPage((page) => Math.min(notificationsTotalPages, page + 1))}
                    disabled={notificationsPage === notificationsTotalPages}
                  >
                    Siguiente
                  </button>
                </div>
              ) : null}
            </section>

            <section className="dash-card">
              <div className="dash-card-head">
                <h2>Ruta de estudio</h2>
                <button className="dash-btn ghost dash-section-btn" onClick={() => navigate("/roadmap")}>Ver ruta</button>
              </div>

              <div className="roadmap">
                {roadmap.map((step) => {
                  const done = !!roadmapProgress?.[step.id]?.completed;

                  return (
                    <div
                      key={step.id}
                      className={`road-step ${done ? "done" : ""}`}
                      onClick={() => navigate(`/roadmap/${step.id}`)}
                    >
                      <div className="dot">{done ? "✓" : ""}</div>
                      <div className="road-text">{step.title}</div>
                    </div>
                  );
                })}
              </div>

              <div className="muted small road-next">
                Siguiente recomendado: <strong>{nextRoadmap}</strong>
              </div>
            </section>

            <section className="dash-card">
              <div className="dash-card-head">
                <h2>Herramientas</h2>
                <button className="dash-btn ghost dash-section-btn" onClick={() => navigate("/aprende?academy=1")}>Ver todo</button>
              </div>

              <div className="tools-grid">
                {tools.map((t) =>
                  t.link ? (
                    <a
                      key={t.id}
                      className="tool-card tool-card-link"
                      href={t.link}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <div className="tool-title">{t.name}</div>
                      <div className="muted small">{t.desc}</div>
                    </a>
                  ) : (
                    <div key={t.id} className="tool-card">
                      <div className="tool-title">{t.name}</div>
                      <div className="muted small">{t.desc}</div>
                    </div>
                  )
                )}
              </div>
            </section>

          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
